import type { PortalSiteDisplaySettingsDto } from '@supply-mover-2-0/shared';
import { usePortalAuthStore } from '../stores/portalAuthStore';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Effective storefront display flags for the current visitor.
 *
 * Authenticated /auth/me supersedes the public bootstrap when present.
 * Falls back to the bootstrap (anonymous-visitor flags) and finally to a
 * permissive logged-in default so logic that consults this hook before
 * either response lands doesn't render an empty product card.
 *
 * IMPORTANT: These are UI hints. The API independently strips fields
 * (price, contractPrice, inStock) from responses based on the same
 * underlying portal_site.settings — never rely on this hook for security.
 */
const DEFAULT_AUTH_FALLBACK: PortalSiteDisplaySettingsDto = {
	isAnonymousVisitor: false,
	canSeePricing: true,
	canSeeStockAvailability: true,
	canSeeStockQuantity: false,
	canBrowseCatalog: true,
	canPurchase: true,
	canViewOrderHistory: true,
	canViewInvoices: true,
	canPayInvoices: true,
	// Match the API default (`portal-site-context.service.ts`) — landing
	// authenticated users on the dashboard is the campaign-promoted
	// home. Tenants that prefer browse-first opt back via the portal_site
	// row.
	defaultAuthenticatedView: 'dashboard',
};

export function useDisplaySettings(): PortalSiteDisplaySettingsDto {
	const authDisplay = usePortalAuthStore((s) => s.displaySettings);
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const bootstrapDisplay = usePortalSiteStore(
		(s) => s.site?.displaySettings ?? null,
	);

	if (isAuthenticated && authDisplay != null) {
		return authDisplay;
	}

	if (bootstrapDisplay != null) {
		return bootstrapDisplay;
	}

	return DEFAULT_AUTH_FALLBACK;
}
